import React, { useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, Cell } from "recharts";

const depthColor = (d) => (d < 50 ? "#ef4444" : d <= 300 ? "#f97316" : "#10b981");

export default function CrossSection({ open, onOpenChange, quakes }) {
  const data = useMemo(
    () => quakes.filter((q) => q.magnitude >= 3).map((q) => ({
      x: q.longitude,
      y: -q.depth,
      mag: q.magnitude,
      z: Math.max(30, q.magnitude * 40),
      location: q.location,
      depth: q.depth,
    })),
    [quakes]
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby={undefined} className="max-w-5xl border-2 border-slate-700 bg-slate-900 text-slate-100 shadow-2xl shadow-cyan-500/10" data-testid="cross-section-modal">
        <DialogHeader>
          <DialogTitle>Cross-Section Depth Profile (0–700 km)</DialogTitle>
        </DialogHeader>
        <div className="text-xs text-slate-400 mb-2">
          Distribusi hiposenter berdasarkan bujur (X) dan kedalaman (Y). Zona subduksi tampak sebagai kemiringan sudut menunjam.
        </div>
        <div className="h-[420px] w-full rounded-lg border border-slate-800 bg-slate-900/60 p-3">
          <ResponsiveContainer width="100%" height="100%">
            <ScatterChart margin={{ top: 10, right: 20, bottom: 30, left: 30 }}>
              <CartesianGrid stroke="#1e293b" strokeDasharray="3 3" />
              <XAxis type="number" dataKey="x" name="Longitude" stroke="#64748b" tick={{ fill: "#94a3b8", fontSize: 11, fontFamily: "JetBrains Mono" }} label={{ value: "Longitude °", fill: "#94a3b8", position: "bottom", offset: 10 }} />
              <YAxis type="number" dataKey="y" name="Depth" domain={[-700, 0]} stroke="#64748b" tick={{ fill: "#94a3b8", fontSize: 11, fontFamily: "JetBrains Mono" }} label={{ value: "Depth (km)", fill: "#94a3b8", angle: -90, position: "insideLeft", offset: -10 }} tickFormatter={(v) => Math.abs(v)} />
              <ReferenceLine y={-50} stroke="#ef4444" strokeDasharray="3 3" opacity={0.4} label={{ value: "50 km", fill: "#ef4444", fontSize: 10, position: "right" }} />
              <ReferenceLine y={-300} stroke="#f97316" strokeDasharray="3 3" opacity={0.4} label={{ value: "300 km", fill: "#f97316", fontSize: 10, position: "right" }} />
              <Tooltip
                contentStyle={{ background: "#0f172a", border: "1px solid #334155", borderRadius: 8, fontSize: 12 }}
                labelStyle={{ color: "#e2e8f0" }}
                formatter={(_, __, item) => {
                  const p = item.payload;
                  return [`M ${p.mag.toFixed(1)} @ ${p.depth.toFixed(1)}km`, p.location];
                }}
              />
              <Scatter data={data}>
                {data.map((d, i) => (<Cell key={i} fill={depthColor(d.depth)} />))}
              </Scatter>
            </ScatterChart>
          </ResponsiveContainer>
        </div>
        <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
          <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2">
            <div className="font-mono font-bold text-red-400">&lt; 50 km</div>
            <div className="text-red-300/80">Dangkal · Risiko permukaan tinggi</div>
          </div>
          <div className="rounded-lg border border-orange-500/30 bg-orange-500/10 px-3 py-2">
            <div className="font-mono font-bold text-orange-400">50–300 km</div>
            <div className="text-orange-300/80">Menengah · Zona Wadati-Benioff</div>
          </div>
          <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-3 py-2">
            <div className="font-mono font-bold text-emerald-400">&gt; 300 km</div>
            <div className="text-emerald-300/80">Dalam · Mantel atas</div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
